import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { SignatureField } from '@symphenia/ui-components'
import type { SiteDocPublicCountersign, SiteDocPublicCountersignItem } from '@symphenia/shared-types'
import { fehlertext } from '@/lib/fehlertext'
import { fmtDate } from '@/lib/format-date'
import { useTerminology } from '@/lib/useTerminology'
import { cn } from '@/lib/utils'
import { alsDataUrl, publicCountersignFotoUrl, publicCountersignObject, publicCountersignOpen, publicCountersignSign, publicCountersignView } from '../api/site-docs.api'
import { KanonAnsicht } from '../components/KanonAnsicht'
import '../arten'

const feld = 'h-11 w-full rounded-md border border-input bg-background px-3 text-base'

const ZUSTAND_KLASSE: Record<SiteDocPublicCountersignItem['state'], string> = {
  OPEN: 'bg-muted text-foreground',
  SIGNED: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100',
  OBJECTED: 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100',
  VOIDED: 'bg-red-100 text-red-900 dark:bg-red-900/40 dark:text-red-100',
}

/** 410 — der Link ist inzwischen abgelaufen. */
const abgelaufen = (e: unknown) => (e as { response?: { status?: number } })?.response?.status === 410

/**
 * AP-48 6.3 (RN-6) — **Sammel-Gegenzeichnung** im Browser der Bauaufsicht, ohne Anmeldung: Kopf mit Betrieb,
 * Projekten und Frist; die Liste der Dokumente, jedes aufklappbar zur Vorzeige aus dem kanonischen Stand samt
 * Fotos. „Alle gegenzeichnen" erst, wenn jedes offene Dokument einmal offen war — eine Zeichnung, je Dokument
 * eine Unterschrift. Einzeln: „Gegenzeichnen" oder „Einwand" mit Pflichttext. Ein storniertes Dokument ist
 * nicht mehr zeichenbar.
 */
export default function GegenzeichnenPage() {
  const { t } = useTranslation()
  const terms = useTerminology()
  const { token = '' } = useParams<{ token: string }>()
  const [stand, setStand] = useState<SiteDocPublicCountersign | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [offen, setOffen] = useState<Set<string>>(new Set())
  const [zeichnen, setZeichnen] = useState<string[] | null>(null)
  const [einwand, setEinwand] = useState<string | null>(null)
  const [einwandText, setEinwandText] = useState('')
  const [name, setName] = useState('')
  const [funktion, setFunktion] = useState('')
  const [bild, setBild] = useState<Blob | null>(null)
  const [arbeitet, setArbeitet] = useState(false)

  useEffect(() => {
    publicCountersignView(token)
      .then((s) => {
        setStand(s)
        if (s.name) setName(s.name)
        if (s.function) setFunktion(s.function)
      })
      .catch((e) => setFehler(fehlertext(e, t)))
  }, [token, t])

  const tue = async (f: () => Promise<SiteDocPublicCountersign>) => {
    setArbeitet(true)
    setFehler(null)
    try {
      setStand(await f())
      return true
    } catch (e) {
      if (abgelaufen(e)) setStand((s) => (s ? { ...s, state: 'EXPIRED', items: [] } : s))
      else setFehler(fehlertext(e, t))
      return false
    } finally {
      setArbeitet(false)
    }
  }

  const klappe = (i: SiteDocPublicCountersignItem) => {
    const auf = !offen.has(i.id)
    setOffen((o) => {
      const n = new Set(o)
      if (auf) n.add(i.id)
      else n.delete(i.id)
      return n
    })
    // Beim ersten Aufklappen merkt sich der Server „geöffnet" — erst danach lässt es sich zeichnen.
    if (auf && i.state === 'OPEN' && !i.opened) void tue(() => publicCountersignOpen(token, i.id))
  }

  const offene = stand?.items.filter((i) => i.state === 'OPEN') ?? []
  const alleGeoeffnet = offene.length > 0 && offene.every((i) => i.opened)
  const datum = (iso: string | null) => (iso ? fmtDate(iso.slice(0, 10)) : '')

  return (
    <div className="min-h-screen bg-background px-4 py-6 text-foreground">
      <main className="mx-auto max-w-3xl space-y-4">
        {stand?.tenantName && <p className="text-sm font-medium text-muted-foreground">{stand.tenantName}</p>}
        <h1 className="text-2xl font-bold">{t('siteDocs.publicCountersign.title')}</h1>
        {fehler && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</p>}
        {!stand && !fehler && <p className="text-muted-foreground">{t('common.loading')}</p>}
        {stand?.state === 'UNKNOWN' && <p>{t('siteDocs.public.unknown')}</p>}
        {stand?.state === 'EXPIRED' && <p>{t('siteDocs.public.expired', { betrieb: stand.tenantName ?? '' })}</p>}
        {stand?.state === 'DONE' && <p className="text-lg">{t('siteDocs.publicCountersign.done')}</p>}

        {stand && (stand.state === 'OPEN' || stand.state === 'DONE') && (
          <>
            <dl className="space-y-0.5 text-sm">
              {stand.projects.length > 0 && (
                <div className="flex gap-2">
                  <dt className="text-muted-foreground">{terms.project}:</dt>
                  <dd>{stand.projects.join(', ')}</dd>
                </div>
              )}
              {(stand.name || stand.role) && (
                <div className="flex gap-2">
                  <dt className="text-muted-foreground">{t('siteDocs.publicCountersign.for')}:</dt>
                  <dd>{[stand.name, stand.function, stand.role ? t(`siteDocs.roles.${stand.role}`) : null].filter(Boolean).join(' · ')}</dd>
                </div>
              )}
              {stand.state === 'OPEN' && stand.expiresAt && (
                <div className="flex gap-2">
                  <dt className="text-muted-foreground">{t('siteDocs.confirmation.validUntil')}:</dt>
                  <dd>{datum(stand.expiresAt)}</dd>
                </div>
              )}
            </dl>
            {stand.state === 'OPEN' && <p className="text-sm text-muted-foreground">{t('siteDocs.publicCountersign.intro')}</p>}

            <ul className="space-y-2">
              {stand.items.map((i) => {
                const auf = offen.has(i.id)
                return (
                  <li key={i.id} className="rounded-md border">
                    <button type="button" className="flex w-full flex-wrap items-center gap-2 px-3 py-2 text-left" aria-expanded={auf} disabled={i.state === 'VOIDED'} onClick={() => klappe(i)}>
                      {i.state !== 'VOIDED' && (auf ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />)}
                      <span className="font-medium tabular-nums">{i.number ?? '—'}</span>
                      <span className="text-sm text-muted-foreground">{datum(i.documentDate)}</span>
                      <span className="min-w-0 flex-1 truncate text-sm">{i.title}</span>
                      <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', ZUSTAND_KLASSE[i.state])}>{t(`siteDocs.publicCountersign.states.${i.state}`)}</span>
                    </button>
                    {i.state === 'OBJECTED' && i.objection && <p className="border-t px-3 py-2 text-sm">{t('siteDocs.publicCountersign.yourObjection', { text: i.objection })}</p>}
                    {auf && i.objekt && (
                      <div className="border-t">
                        <KanonAnsicht objekt={i.objekt} />
                        {i.photos.length > 0 && (
                          <div className="grid grid-cols-2 gap-2 px-4 pb-4 sm:grid-cols-3">
                            {i.photos.map((f) => (
                              <figure key={f.id} className="space-y-1">
                                <img src={publicCountersignFotoUrl(token, f.id)} alt={f.caption ?? ''} className="w-full rounded-md border object-cover" loading="lazy" />
                                {f.caption && <figcaption className="text-xs text-muted-foreground">{f.caption}</figcaption>}
                              </figure>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {i.state === 'OPEN' && stand.state === 'OPEN' && (
                      <div className="flex flex-wrap gap-2 border-t px-3 py-2">
                        <button type="button" disabled={!i.opened || arbeitet} title={i.opened ? undefined : t('siteDocs.publicCountersign.openFirst')} onClick={() => { setEinwand(null); setZeichnen([i.id]) }} className="h-10 rounded-md border px-3 text-sm disabled:opacity-50">
                          {t('siteDocs.publicCountersign.signOne')}
                        </button>
                        <button type="button" disabled={arbeitet} onClick={() => { setZeichnen(null); setEinwand(i.id); setEinwandText('') }} className="h-10 rounded-md border px-3 text-sm text-amber-800 dark:text-amber-200">
                          {t('siteDocs.publicCountersign.object')}
                        </button>
                        {!i.opened && <span className="self-center text-xs text-muted-foreground">{t('siteDocs.publicCountersign.openFirst')}</span>}
                      </div>
                    )}
                    {einwand === i.id && (
                      <section className="space-y-2 border-t px-3 py-3">
                        <textarea className="min-h-24 w-full rounded-md border border-input bg-background p-3 text-base" aria-label={t('siteDocs.publicCountersign.objectionText')} placeholder={t('siteDocs.publicCountersign.objectionText')} value={einwandText} onChange={(e) => setEinwandText(e.target.value)} />
                        <input className={feld} aria-label={t('siteDocs.release.name')} placeholder={t('siteDocs.release.name')} value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
                        <div className="flex flex-wrap gap-2">
                          <button type="button" onClick={() => setEinwand(null)} className="h-11 rounded-md border px-4 text-sm">
                            {t('common.cancel')}
                          </button>
                          <button
                            type="button"
                            disabled={!einwandText.trim() || arbeitet}
                            onClick={async () => {
                              if (await tue(() => publicCountersignObject(token, { itemId: i.id, text: einwandText.trim(), name: name.trim() || null }))) setEinwand(null)
                            }}
                            className="h-11 rounded-md bg-amber-600 px-4 text-sm font-medium text-white disabled:opacity-50"
                          >
                            {t('siteDocs.publicCountersign.sendObjection')}
                          </button>
                        </div>
                      </section>
                    )}
                  </li>
                )
              })}
            </ul>

            {stand.state === 'OPEN' && offene.length > 1 && !zeichnen && (
              <div className="flex flex-wrap items-center justify-end gap-2">
                {!alleGeoeffnet && <span className="text-xs text-muted-foreground">{t('siteDocs.publicCountersign.openAllFirst')}</span>}
                <button type="button" disabled={!alleGeoeffnet || arbeitet} onClick={() => { setEinwand(null); setZeichnen(offene.map((i) => i.id)) }} className="h-11 rounded-md bg-primary px-5 text-sm font-medium text-primary-foreground disabled:opacity-50">
                  {t('siteDocs.publicCountersign.signAll', { count: offene.length })}
                </button>
              </div>
            )}

            {zeichnen && stand.state === 'OPEN' && (
              <section className="space-y-3 rounded-md border p-3">
                <p className="text-sm font-medium">
                  {t('siteDocs.publicCountersign.signing', {
                    liste: zeichnen
                      .map((id) => stand.items.find((x) => x.id === id)?.number)
                      .filter(Boolean)
                      .join(', '),
                  })}
                </p>
                <div className="grid gap-2 sm:grid-cols-2">
                  <input className={feld} aria-label={t('siteDocs.release.name')} placeholder={t('siteDocs.release.name')} value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
                  <input className={feld} aria-label={t('siteDocs.release.function')} placeholder={t('siteDocs.release.function')} value={funktion} onChange={(e) => setFunktion(e.target.value)} />
                </div>
                <SignatureField onChange={setBild} label={t('siteDocs.release.signature')} loeschenLabel={t('siteDocs.release.clear')} zuKurzLabel={t('siteDocs.release.tooShort')} />
                <div className="flex flex-wrap justify-end gap-2">
                  <button type="button" onClick={() => setZeichnen(null)} className="h-11 rounded-md border px-4 text-sm">
                    {t('common.cancel')}
                  </button>
                  <button
                    type="button"
                    disabled={!name.trim() || !bild || arbeitet}
                    onClick={async () => {
                      const ids = zeichnen
                      if (await tue(async () => publicCountersignSign(token, { itemIds: ids, name: name.trim(), function: funktion.trim() || null, image: await alsDataUrl(bild!) }))) setZeichnen(null)
                    }}
                    className="h-11 rounded-md bg-primary px-5 text-sm font-medium text-primary-foreground disabled:opacity-50"
                  >
                    {t('siteDocs.publicCountersign.sign')}
                  </button>
                </div>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  )
}
